import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import protectionProgramService from "../../../../../services/web/protection-program-service.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {FaCopy, FaPlus, FaTimes} from "react-icons/fa";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import ModalImage from "../../../../../components/modal/modal-image.jsx";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import roleService from "../../../../../services/web/role-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {getStreets} from "../../../../../utils/location-utils.js";
import optionsUtils from "../../../../../utils/options-utils.js";
import {MaskDefault} from "../../../../../utils/mask.js";
import Alert from "../../../../../components/alert/alert.jsx";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const ProtectionProgramForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const [isSaving, setIsSaving] = useState(false);

    const initialState = useMemo(() => ({
        name: "",
        protectiveMeasure: false,
        imageId: "",
        file: null,
        roles: [],
        phone: "",
        address: null,
        number: "",
        alternativePhone: ""
    }), []);

    const [imageModal, setImageModal] = useState({open: false, imgScr: null});
    const [state, setState] = useState(initialState);
    const [imgScr, setImgScr] = useState(null);

    const {data, refetch} = useQuery({
        queryFn: () => protectionProgramService.get(id),
        queryKey: ['program'],
        enabled: !!id,
        placeholderData: undefined
    });

    const {data: roles} = useQuery({
        queryFn: () => roleService.getByPermission().then(({data}) => data.map(({id, text}) => ({value: id, label: text}))),
        queryKey: ['roles']
    });

    useEffect(() => {
        setState(initialState);
    }, [id, initialState])

    useEffect(() => {
        if (!id || !data) return;

        setState({
            name: data.name,
            protectiveMeasure: data.protectiveMeasure,
            imageId: data.imageId,
            file: null,
            roles: data?.roles.map(({id, name}) => ({value: id, label: name})),
            phone: data?.phone,
            address: data?.address,
            number: data?.number,
            alternativePhone: data?.alternativePhone
        });
    }, [id, data]);

    const imgRef = useRef(undefined);

    const triggerInput = () => imgRef.current?.click();

    const attachUserImage = ({target: {files}}) => {
        const file = files[0];
        const reader = new FileReader();

        if (file.size >= 2000000) {
            let message = 'Tamanho máximo de arquivo excedido. Limite: 2MB.';
            projectUtils.swalErrorMessage('', message, 'Ok');
            return;
        }

        reader.onload = function (e) {
            setImgScr(e.target.result);
        }

        reader.readAsDataURL(file);
        setState(prev => ({...prev, imageId: null, file}))
    }

    const removeFile = () => {
        setImgScr(null);
        setState(prev => ({...prev, imageId: null, file: null}))
    }

    const openModal = () => {
        if (!state.imageId && !state.file) return;

        setImageModal({open: true, imgScr: state.imageId ? `${API_KEEPER}/ged/attach/${state.imageId}` : imgScr })
    }

    const submit = () => {
        setIsSaving(true)

        const formData = new FormData();
        formData.append("name", state.name);
        formData.append("protectiveMeasure", state.protectiveMeasure);
        formData.append("imageId", state.imageId);
        formData.append("roles", state.roles?.map(r => r.value) || []);
        formData.append("phone", state.phone?.replace(/\D/g, '') ?? "");
        if (state.address) formData.append("address", state.address);
        formData.append("number", state?.number ?? "");
        formData.append("alternativePhone", state.alternativePhone?.replace(/\D/g, '') ?? "");

        if (state.file) formData.append("file", state.file);

        let response;
        if (id) response = protectionProgramService.edit(formData, id);
        else response = protectionProgramService.create(formData);

        response
            .then((data) => {
                projectUtils.success(!id)

                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/sos/protection-program/form/${data}`;
                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = translateErrMessage(error.response.data.message, message);
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            }).finally(() => setIsSaving(false))
    }

    const deleteProgram = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir programa "${state.name ?? ""}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    protectionProgramService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/sos/protection-program");
                        })
                        .catch((error) => {
                            console.error(error);
                            let message = 'Não foi possível excluir.';
                            if (error.response.status !== 500) {
                                message = translateErrMessage(error.response.data, message);
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok')
                        })
                }
            }
        );
    }

    const translateErrMessage = (message, defaultMessage) => {
        const err = {
            "program.in.use": "Esse item possui um cidadão vinculado.",
            "missing.name": "O campo \"Nome\" é obrigatório.",
            "name.already.used": "Este \"Nome\" já está sendo utilizado."
        }

        return err[message] ?? defaultMessage
    }

    return (
        <>
            <Template>
                <Template.Header>
                    <Template.Title>Programa de proteção</Template.Title>

                    {
                        id &&
                        <Button target={"default"} onClick={() => navigate("/sos/protection-program/form")}>
                            <FaPlus/> Adicionar programa
                        </Button>
                    }
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body className={"space-y-4 grid grid-cols-12 gap-4"}>
                            <section className={"col-span-3"}>
                                <div className="relative">
                                    {
                                        (state.imageId|| imgScr) &&
                                        <Button target={"danger"} className={"absolute !w-[25px] !h-[25px] !p-0 right-[5px] top-[5px]"} onClick={() => removeFile()} title="Remover imagem">
                                            <FaTimes />
                                        </Button>
                                    }
                                    <div className="overflow-hidden h-[158px] bg-center bg-no-repeat bg-contain cursor-pointer border border-[#e0e0e0] rounded p-1" id="image-user" onClick={openModal}
                                         style={{
                                             backgroundImage: state.imageId ? `url(${API_KEEPER}/ged/attach/${state.imageId})` : imgScr ? `url(${imgScr})` : `url(${getAssetPath("img/no-img.png")})`,
                                         }}
                                    >
                                    </div>
                                    <input type="file" accept="image/png, image/jpeg" className="hidden" name="image" onChange={attachUserImage} ref={imgRef}/>
                                    <Button className={"w-full"} onClick={triggerInput}>
                                        <span>Selecionar foto</span>
                                    </Button>
                                    <small>* Tamanho máximo 2MB</small>
                                </div>
                            </section>
                            <SentryRow className={"col-span-9 !self-start"}>
                                <InputForm
                                    id={"name"}
                                    label={"Nome *"}
                                    value={state.name}
                                    classNameWrapper={"col-span-6"}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, name: value}))}
                                />

                                <section className={"flex justify-between gap-4 col-span-6"}>
                                    <InputForm
                                        id={"externalId"}
                                        classNameWrapper={"w-full"}
                                        defaultValue={data?.externalId}
                                        disabled={true}
                                        label={"ID Externo"}
                                    />

                                    <Button title={"Copiar ID Externo"} onClick={() => projectUtils.copyToClipboard(data?.externalId, "ID Externo copiado.")}>
                                        <FaCopy />
                                    </Button>
                                </section>

                                <SelectComponent
                                    value={state.roles}
                                    options={roles ?? []}
                                    label={"Funções que receberão o alerta no app Sentry Agente"}
                                    classNameWrapper={"col-span-6"}
                                    onChange={(value) => setState(prev => ({...prev, roles: value}))}
                                    isMulti
                                />

                                <InputCheckbox
                                    className={"col-span-6"}
                                    id={"protectiveMeasure"}
                                    checked={state.protectiveMeasure}
                                    label={"Medida protetiva"}
                                    onChange={({target: {checked}}) => setState(prev => ({...prev, protectiveMeasure: checked}))}
                                />

                                <SelectComponent
                                    classNameWrapper={"col-span-3"}
                                    loadOptions={getStreets}
                                    value={optionsUtils.getValue(state.address)}
                                    isAsync={true}
                                    label="Logradouro"
                                    onChange={(selected) => setState(prev => ({...prev, address: selected?.value}))}
                                    cacheOptions
                                    defaultOptions={[]}
                                />

                                <InputForm
                                    id={"number"}
                                    label={"Número"}
                                    value={state.number}
                                    classNameWrapper={"col-span-3"}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, number: value}))}
                                />

                                <InputForm
                                    id={"phone"}
                                    label={"Telefone"}
                                    value={state.phone}
                                    classNameWrapper={"col-span-3"}
                                    mask={MaskDefault.phone}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, phone: value}))}
                                />

                                <InputForm
                                    id={"alternativePhone"}
                                    label={"Telefone alternativo"}
                                    value={state.alternativePhone}
                                    classNameWrapper={"col-span-3"}
                                    mask={MaskDefault.phone}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, alternativePhone: value}))}
                                />
                            </SentryRow>

                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body className={"flex justify-end gap-2"}>
                            {
                                id &&
                                <>
                                    {
                                        data && data?.inUse &&
                                        <Alert className={"flex gap-2 items-center h-[32px]!"} target={"warning"}>
                                            Não é possível excluir, pois existem cidadãos dependentes do programa.
                                        </Alert>
                                    }

                                    <Button target={"danger"} onClick={deleteProgram} disabled={data?.inUse} actionFeedbackType="delete">
                                        Excluir
                                    </Button>
                                </>
                            }
                            <Button onClick={submit} loading={isSaving} actionFeedbackType="save">
                                Salvar
                            </Button>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <ModalImage
                open={imageModal.open}
                setOpen={() => setImageModal({open: false, imgScr: null})}
                imgUrl={imageModal.imgScr}
            />
        </>
    );
};

export default ProtectionProgramForm;